import { observer } from "mobx-react";
import type React from "react";
import { useTranslation } from "react-i18next";

import { useDomainHandler } from "hooks/useDomainHandler";
import { useGroups } from "hooks/useGroups";

import type { GroupStudent } from "models/groups/Group";

import { CssButton, CssIcon } from "styles/helpers/layout";
import { StyledTable } from "./GroupMembers.styled";

interface GroupMembersProps {
  groupId: number;
  students: GroupStudent[];
}

export const GroupMembers: React.FC<GroupMembersProps> = observer(({ groupId, students }) => {
  const { t } = useTranslation(["common", "groups"]);
  const { getStudentTermByDomain } = useDomainHandler();
  const { removeStudentsFromGroup, removingStudents: removingStudentsIds } = useGroups();

  const removeStudent = (studentId: number) => {
    removeStudentsFromGroup(groupId, [studentId]);
  };

  return (
    <StyledTable data-cy="group-students-table">
      <thead>
        <tr>
          <td>
            {t("groups:groupDetails.groupMembers.name", {
              studentTerm: getStudentTermByDomain(),
            })}
          </td>
          <td>{t("groups:groupDetails.groupMembers.email")}</td>
          <td>{t("groups:groupDetails.groupMembers.actions")}</td>
        </tr>
      </thead>
      <tbody>
        {students.map((student: GroupStudent) => (
          <tr data-cy="student-row" key={student.id}>
            <td data-cy="student-name">{student.name}</td>
            <td data-cy="student-email">{student.email}</td>
            <td>
              <CssButton
                aria-label={t("common:iconButtonLabel.delete")}
                disabled={removingStudentsIds.includes(student.id)}
                onClick={() => removeStudent(student.id)}
              >
                <CssIcon data-cy="delete-student-button" iconName="fas fa-trash" />
              </CssButton>
            </td>
          </tr>
        ))}
      </tbody>
    </StyledTable>
  );
});
